import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { SpeakerWaveIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

/**
 * TestSpeakersButton — plays a short chime through the chosen output device so
 * the user can confirm they'll hear the call.
 *
 * There's no VideoSDK "changeSpeaker", so we route the sound ourselves: an
 * Oscillator feeds a MediaStreamDestination, that stream is attached to a
 * hidden <audio>, and setSinkId() pins playback to `deviceId`. That makes the
 * device choice actually take effect (setSinkId only exists on media elements,
 * not on the Web Audio graph). Fully self-contained — no audio asset needed.
 *
 * @param {string} [deviceId]  Output device id from getPlaybackDevices().
 * @param {string} [label]     Button text. Default "Test Speakers".
 * @param {string} [className]
 */
export default function TestSpeakersButton({ deviceId, label, className }) {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const cleanupRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  // Tear down any in-flight audio graph if we unmount mid-chime.
  useEffect(() => () => cleanupRef.current?.(), []);

  const handleTest = async () => {
    if (playing) return;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const el = audioRef.current;
    if (!AudioCtx || !el) return;

    setPlaying(true);
    const ctx = new AudioCtx();
    const dest = ctx.createMediaStreamDestination();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.connect(gain);
    gain.connect(dest);

    const finish = () => {
      try {
        el.pause();
        el.srcObject = null;
      } catch (e) {
        /* noop */
      }
      if (ctx.state !== "closed") ctx.close().catch(() => {});
      cleanupRef.current = null;
      setPlaying(false);
    };
    cleanupRef.current = finish;

    try {
      el.srcObject = dest.stream;
      // Pin output to the selected speaker when the browser supports it.
      if (deviceId && typeof el.setSinkId === "function") {
        await el.setSinkId(deviceId).catch(() => {});
      }
      await el.play().catch(() => {});

      const now = ctx.currentTime;
      // Soft two-note chime (E5 -> A5) with a quick fade in/out.
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.15, now + 0.03);
      osc.frequency.setValueAtTime(659.25, now);
      osc.frequency.setValueAtTime(880, now + 0.18);
      gain.gain.setValueAtTime(0.15, now + 0.34);
      gain.gain.linearRampToValueAtTime(0, now + 0.42);
      osc.start(now);
      osc.stop(now + 0.44);
      osc.onended = finish;
    } catch (e) {
      finish();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleTest}
        disabled={playing}
        className={clsx(
          "flex w-full items-center justify-center gap-2 rounded-lg bg-orange-150 px-4 py-2.5 text-sm font-semibold text-white",
          "transition-opacity hover:bg-orange-150/90 disabled:cursor-not-allowed disabled:opacity-70",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-150/60",
          className,
        )}
      >
        <SpeakerWaveIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {playing ? t("Playing…") : label || t("Test Speakers")}
      </button>
      {/* Hidden sink for setSinkId-based output routing. */}
      <audio ref={audioRef} className="hidden" playsInline />
    </>
  );
}
